/**
 * Normalizes a phone number to the format: +40727761112
 *
 * @param phone - The phone number to normalize
 * @returns The normalized phone number in format +40727761112
 */
export function normalizePhoneNumber(phone: string): string {
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  // If the number starts with 40 (Romania country code), add + prefix
  if (digitsOnly.startsWith('40')) {
    return `+${digitsOnly}`;
  }

  // If the number starts with 0, remove it and add +40 prefix
  if (digitsOnly.startsWith('0')) {
    return `+40${digitsOnly.slice(1)}`;
  }

  // If the number starts with 7, add +40 prefix
  if (digitsOnly.startsWith('7')) {
    return `+40${digitsOnly}`;
  }

  // For any other case, assume it's a local number and add +40 prefix
  return `+40${digitsOnly}`;
}

/**
 * Validates if a phone number is in the correct format
 *
 * @param phone - The phone number to validate
 * @returns true if the phone number is valid, false otherwise
 */
export function isValidPhoneNumber(phone: string): boolean {
  // Check if phone starts with +407 and has exactly 12 digits total
  const normalizedPhone = normalizePhoneNumber(phone);
  return /^\+407\d{8}$/.test(normalizedPhone);
}

/**
 * Formats a phone number for display purposes
 * Example: +40727761112 -> +40 727 761 112
 *
 * @param phone - The phone number to format
 * @returns Formatted phone number with spaces
 */
export function formatPhoneForDisplay(phone: string): string {
  if (!phone) return '';

  // Normalize first
  const normalized = normalizePhoneNumber(phone);

  // If it doesn't match our expected format, return as is
  if (!normalized.startsWith('+40') || normalized.length !== 12) {
    return phone;
  }

  // Format: +40 XXX XXX XXX
  const phoneDigits = normalized.slice(3); // Remove +40
  return `+40 ${phoneDigits.slice(0, 3)} ${phoneDigits.slice(3, 6)} ${phoneDigits.slice(6)}`;
}

/**
 * Masks a phone number for display, hiding the middle group.
 * Example: +40675858499 -> +40 675 *** 499
 *
 * @param phone - The phone number to mask
 * @returns Masked phone number, or the original input if it can't be normalized
 */
export function maskPhoneForDisplay(phone: string): string {
  if (!phone) return '';

  const normalized = normalizePhoneNumber(phone);

  if (!normalized.startsWith('+40') || normalized.length !== 12) {
    return phone;
  }

  const phoneDigits = normalized.slice(3); // Remove +40
  return `+40 ${phoneDigits.slice(0, 3)} *** ${phoneDigits.slice(6)}`;
}

/**
 * Normalizes phone input value for input fields
 * Removes non-digits and strips country code (40) and leading 0
 * since the country code is typically displayed separately in the UI
 *
 * @param value - The raw input value from the phone input field
 * @returns Normalized input value ready for display in the input field
 */
export function normalizePhoneInput(value: string): string {
  const digitsOnly = value.replace(/\D/g, '');

  let processedValue = digitsOnly;

  if (digitsOnly.startsWith('40')) {
    processedValue = digitsOnly.slice(2);
  }

  if (processedValue.startsWith('0')) {
    processedValue = processedValue.slice(1);
  }

  return processedValue;
}
